import { useCallback, useMemo } from 'react';

import {
  ColumnListKey,
  ColumnPreference,
  useGetColumnPreferencesQuery,
  useUpdateColumnPreferencesMutation,
} from 'src/services/settings/column-preferences.service';

import { ColumnDef, PickerColumn, buildPickerModel, resolveColumns } from './data-list.types';

/**
 * Ties a per-entity column registry to the user's saved layout (JT1-1528): resolves the visible,
 * ordered columns for DataList, builds the full picker model, and persists changes. The picker only
 * needs to hand back the non-fixed columns in the desired order with their visibility.
 */
export function useColumnPreferences<TRow>(list: ColumnListKey, registry: ColumnDef<TRow>[]) {
  const { data, isLoading } = useGetColumnPreferencesQuery(list);
  const [updatePreferences] = useUpdateColumnPreferencesMutation();

  const preferences = data?.columns ?? null;

  const columns = useMemo(() => resolveColumns(registry, preferences), [registry, preferences]);
  const pickerModel = useMemo(() => buildPickerModel(registry, preferences), [registry, preferences]);

  const save = useCallback(
    (model: PickerColumn[]) => {
      // Persist every non-fixed column in the chosen order with its visibility; fixed columns are
      // always shown and don't need storing.
      const columnsToSave: ColumnPreference[] = model
        .filter((col) => !col.fixed)
        .map((col) => ({ key: col.key, visible: col.visible }));

      return updatePreferences({ list, columns: columnsToSave }).unwrap().catch(() => undefined);
    },
    [list, updatePreferences]
  );

  return { columns, pickerModel, save, isLoading };
}
